import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, User, Eye, ArrowRight, Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogListingPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blogs?limit=100`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', 'Health & Wellness', 'Buying Guide', 'Technology Comparison', 'Troubleshooting & Maintenance', 'Maintenance', 'Water Quality'];

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-700 to-lime-600 text-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              RO Water Purifier Blog
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8">
              Expert guides, tips, and insights for clean, healthy water
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-6 text-lg rounded-lg border-2 border-white/20 bg-white/10 text-white placeholder:text-white/60 focus:bg-white focus:text-gray-900 focus:placeholder:text-gray-400"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Filter */}
      <div className="bg-white border-b border-gray-200 sticky top-[72px] z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-700 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category === 'all' ? 'All Articles' : category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="container mx-auto px-4 py-16">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
            <p className="mt-4 text-gray-600">Loading articles...</p>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600">No articles found matching your search.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog) => (
              <Card 
                key={blog.id} 
                className="group hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-100 hover:border-blue-200 overflow-hidden"
                onClick={() => navigate(`/blog/${blog.slug}`)}
                data-testid={`blog-card-${blog.slug}`}
              >
                {/* Featured Image */}
                <div className="relative overflow-hidden h-48 bg-gray-200">
                  <img 
                    src={blog.featuredImage} 
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <Badge className="absolute top-4 right-4 bg-blue-700 hover:bg-blue-800 text-white">
                    {blog.category}
                  </Badge>
                </div>

                <CardContent className="p-6">
                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      <span>{formatDate(blog.publishedAt || blog.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye size={16} />
                      <span>{blog.views || 0} views</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors line-clamp-2">
                    <a href={`/blog/${blog.slug}`} onClick={e => e.stopPropagation()} data-testid={`blog-card-link-${blog.slug}`}>{blog.title}</a>
                  </h3>

                  {/* Excerpt */}
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {blog.excerpt}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {blog.tags && blog.tags.slice(0, 3).map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  {/* Read More */}
                  <Button 
                    variant="ghost" 
                    className="text-blue-700 hover:text-blue-800 hover:bg-blue-50 font-semibold p-0 h-auto"
                  >
                    Read More
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-700 to-lime-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Help with Your RO Water Purifier?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Our experts are here to help you choose, install, and maintain your RO system
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/products')}
              size="lg"
              className="bg-white hover:bg-gray-100 text-blue-700 font-bold px-10 py-6 text-lg rounded-lg"
            >
              Browse RO Systems
            </Button>
            <Button 
              onClick={() => navigate('/#contact')}
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-700 font-bold px-10 py-6 text-lg rounded-lg"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Mock blogs data as fallback
const mockBlogs = [
  {
    id: '1',
    title: 'Top 10 Benefits of RO Water Purifier for Your Family\'s Health',
    slug: 'top-10-benefits-of-ro-water-purifier-for-your-family-health',
    excerpt: 'Discover how RO water purifiers can significantly improve your family\'s health by removing harmful contaminants and providing pure, safe drinking water.',
    featuredImage: 'https://images.unsplash.com/photo-1548839140-29a749e1cf4d?w=1200&h=630&fit=crop',
    category: 'Health & Wellness',
    tags: ['RO Water Purifier', 'Health Benefits', 'Water Purification'],
    publishedAt: new Date().toISOString(),
    views: 1250
  },
  {
    id: '2',
    title: 'How to Choose the Right RO Water Purifier for Your Home in 2024',
    slug: 'how-to-choose-right-ro-water-purifier-for-your-home-2024',
    excerpt: 'A comprehensive guide to selecting the perfect RO water purifier based on your water quality, family size, budget, and specific needs.',
    featuredImage: 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=1200&h=630&fit=crop',
    category: 'Buying Guide',
    tags: ['RO Purifier Selection', 'Buying Guide', 'Water Purifier'],
    publishedAt: new Date(Date.now() - 86400000).toISOString(),
    views: 980
  },
  {
    id: '3',
    title: 'RO vs UV vs UF Water Purifiers: Which One is Best for You?',
    slug: 'ro-vs-uv-vs-uf-water-purifiers-comparison',
    excerpt: 'Understand the differences between RO, UV, and UF water purification technologies to make the right choice for your home.',
    featuredImage: 'https://images.unsplash.com/photo-1563207153-f403bf289096?w=1200&h=630&fit=crop',
    category: 'Technology Comparison',
    tags: ['RO Purifier', 'UV Purifier', 'UF Purifier'],
    publishedAt: new Date(Date.now() - 172800000).toISOString(),
    views: 875
  },
  {
    id: '4',
    title: 'Common RO Water Purifier Problems and How to Fix Them',
    slug: 'common-ro-water-purifier-problems-and-solutions',
    excerpt: 'Learn to identify and troubleshoot common RO water purifier issues with our expert guide. Simple solutions to keep your RO running smoothly.',
    featuredImage: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1200&h=630&fit=crop',
    category: 'Troubleshooting & Maintenance',
    tags: ['RO Problems', 'Troubleshooting', 'RO Maintenance'],
    publishedAt: new Date(Date.now() - 259200000).toISOString(),
    views: 1420
  },
  {
    id: '5',
    title: 'Complete RO Water Purifier Maintenance Guide',
    slug: 'complete-ro-water-purifier-maintenance-guide',
    excerpt: 'Essential maintenance tips to extend your RO purifier\'s lifespan and ensure optimal performance. Learn when to replace filters and how to maintain your system.',
    featuredImage: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1200&h=630&fit=crop',
    category: 'Maintenance',
    tags: ['RO Maintenance', 'Filter Replacement', 'Water Purifier Care'],
    publishedAt: new Date(Date.now() - 345600000).toISOString(),
    views: 1100
  },
  {
    id: '6',
    title: 'Understanding TDS in Water: What It Means for Your Health',
    slug: 'understanding-tds-in-water-health-impact',
    excerpt: 'Learn everything about TDS (Total Dissolved Solids) in drinking water, ideal levels, health implications, and how to manage it effectively.',
    featuredImage: 'https://images.unsplash.com/photo-1548839140-29a749e1cf4d?w=1200&h=630&fit=crop',
    category: 'Water Quality',
    tags: ['TDS', 'Water Quality', 'Health'],
    publishedAt: new Date(Date.now() - 432000000).toISOString(),
    views: 1560
  }
];

export default BlogListingPage;
